import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import Sidebar from './HelperSidebar/Sidebar';
import SidebarLink from './HelperSidebar/SidebarLink';
import { Icon } from '../Icon';
import { useUIContext } from '../../context/UIContext';
import Profile from './Profile';
import useRouter from '../../../common/hooks/useRouter';
import styles from './EngageSidebar.module.scss';
import SidebarChatWithUs from './SidebarChatWithUs';
import { useUserContext } from '../../context/UserContext';
import { useTranslation } from 'react-i18next';
import ChangeLanguage from './ChangeLanguage';
import { useLanguageContext } from '../../context/LanguageContext';
import { useMessageContext } from '../../context/MessageContext';
import { useLocation, useHistory } from 'react-router-dom';
import { APP_USER_ROLE } from '../../constants/UserRoles';
import { useSidebarLinks } from 'engage/hooks/useSidebarLinks';
import SidebarLogo from './SidebarLogo';

function ProtectedPageSidebar({ inModal = false, reset = false, inAdvancedEditor = false, fadeIn = false }) {
  const {
    miniSettingLinks,
    toggleMiniSettingLinks,
    mobileSidebar: showMobileSidebar,
    toggleMobileSidebar,
  } = useUIContext();

  const { fetchMessagesOnInit } = useMessageContext();
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const { location } = useRouter();
  const { pathname, state } = location;
  const {
    user,
    userRole,
    isSSOAuthenticationEnabled,
  } = useUserContext();

  const hasManagerPermissions = userRole === APP_USER_ROLE.MANAGER.TYPE || userRole === APP_USER_ROLE.SUPER_ADMIN.TYPE;

  const companyIdParam = new URLSearchParams(search).get('companyId');

  const [height, setHeight] = useState(window.innerHeight);

  const { availableLinks } = useSidebarLinks();

  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const { isLanguageMenu, setIsLanguageMenu, language, setLanguage, languages, setCurrentLanguage } = useLanguageContext();

  const getGoBackPath = useCallback(() => {
    if (!inAdvancedEditor) return null;

    const segments = pathname.split('/');
    const idx = segments.indexOf('advanced-editor');
    if (idx === -1 || idx + 1 >= segments.length) return '/eg/home';
    if (segments[idx + 1] === 'templates') return '/eg/company-settings';
    const videoId = segments[idx + 1];
    return videoId ? `/eg/video/${videoId}` : '/eg/home';
  }, [inAdvancedEditor, pathname]);

  const goBackPath = getGoBackPath();

  const sidebarLinks = useMemo(() => {
    if (!inAdvancedEditor || !goBackPath) {
      return availableLinks.filter(link => !link.hide);
    }

    const goBackLink = {
      name: 'general.back',
      key: 'go_back',
      icon: <Icon src="back" verticalAlign="middle" style={{ fontSize: '24px', transform: 'rotate(180deg)' }} />,
      ariaLabel: "Go back",
      path: goBackPath,
    };

    return [goBackLink, ...availableLinks.filter(link => !link.hide)];
  }, [inAdvancedEditor, goBackPath, availableLinks]);

  const routerOnClick = useMemo(() => {
    if (showMobileSidebar) return { onClick: () => { toggleMobileSidebar(); console.log('routerOnClick') } };
    return {};
  }, [showMobileSidebar, toggleMobileSidebar]);

  const updateDimensions = () => {
    setHeight(window.innerHeight);
  };

  useEffect(() => {
    setCurrentLanguage();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const shouldFetchList = queryParams.get('id') === null;

    if (user) {
      if (shouldFetchList) {
        fetchMessagesOnInit();
      }
    }
  }, [user]);

  useEffect(() => {
    if (reset) {
      setIsOpenMenu(false);
      setIsLanguageMenu(false);
    }
  }, [reset])

  const checkState = useCallback((path) => {
    if (state && Object.keys(state)?.length) {
      if (typeof path == 'object') {
        for (const key in path?.state) {
          return state.hasOwnProperty(key);
        }
        return true;
      } else {
        return false;
      }
    }
    return pathname === path;
  }, [state, pathname]);

  return (
    <Sidebar
      className={cx(styles.sidebar, { [styles.hide]: !showMobileSidebar, [styles.inModal]: inModal })}
      linksClassName={cx({ [styles.inModalLinks]: inModal })}
      footerClassName={cx({ [styles.inModalFooter]: inModal })}
      height={height}
      isMobile={showMobileSidebar}
      fadeIn={fadeIn}
      logo={<SidebarLogo mini={!showMobileSidebar} link={user ? true : false} />}
      links={
        sidebarLinks.map(link => {
          const isGoBack = link.key === 'go_back';
          return <SidebarLink
            key={link.key}
            active={checkState(link.path)}
            component={Link}
            to={link.path}
            icon={link.icon}
            iconOnly={!showMobileSidebar}
            showPopover={!showMobileSidebar}
            aria-label={link.ariaLabel}
            mini={!showMobileSidebar}
            {...(link?.action ? { onMouseDown: link.action, onTouchStart: link.action } : {})}
            {...(isGoBack
              ? {
                  onClick: (e) => {
                    e.preventDefault();
                    // history.action is 'POP' on a fresh page load (direct URL, refresh,
                    // bookmark). Only use goBack() when we arrived via an in-app navigation
                    // (PUSH/REPLACE) so we don't leave the app or do nothing.
                    if (history.action !== 'POP') {
                      history.goBack();
                    } else {
                      history.push(link.path);
                    }
                    if (showMobileSidebar) toggleMobileSidebar();
                  },
                }
              : routerOnClick
            )}
          >
            <span className={styles.labelAdjustment}>
              {t(link.name?.literal ? link.name?.literal : link.name)}
              {link.name?.addition && showMobileSidebar && link.name?.addition}
            </span>
          </SidebarLink>;
        })
      }
      footer={
        <>
          {isLanguageMenu && (
            <ChangeLanguage
              languages={languages}
              language={language}
              setLanguage={setLanguage}
              isLanguageMenu={isLanguageMenu}
              setIsLanguageMenu={setIsLanguageMenu}
              setIsOpenMenu={setIsOpenMenu}
              className={cx({ [styles.inModalLanguages]: inModal })}
            />
          )}

          {user && hasManagerPermissions && !isSSOAuthenticationEnabled && (
            <SidebarLink
              customSidebarStyles={styles.footerSidebarlink}
              active={pathname === '/eg/company-settings' && state?.option === 'USERS'}
              component={Link}
              to={{
                pathname: '/eg/company-settings',
                ...(companyIdParam ? { search: `companyId=${companyIdParam}` } : {}),
                state: { option: 'USERS', openCreateUser: true },
              }}
              icon={<Icon src="invite" verticalAlign="middle" style={{ fontSize: '24px' }} />}
              iconOnly={!showMobileSidebar}
              showPopover={!showMobileSidebar}
              aria-label={t('sidebar.invite_colleague')}
              mini={!showMobileSidebar}
              {...routerOnClick}
            >
              <span>{t('sidebar.invite_colleague')}</span>
            </SidebarLink>
          )}

          {user ? (
            <Profile
              className={styles.profileSection}
              user={user}
              mini={!showMobileSidebar}
              toggleMiniSettingLinks={toggleMiniSettingLinks}
              languages={languages}
              language={language}
              setIsLanguageMenu={setIsLanguageMenu}
              isOpenMenu={isOpenMenu}
              popover={showMobileSidebar ? null : t('sidebar.profile.account_settings')}
              setIsOpenMenu={setIsOpenMenu}
              miniSettingLinks={miniSettingLinks}
              position={inModal ? 'bottomRight' : null}
              isShowCompanySettings={hasManagerPermissions}
              {...routerOnClick}
            />
          ) : (
            <Profile
              user={user}
              active={pathname === '/eg/settings'}
                mini={!showMobileSidebar}
              toggleMiniSettingLinks={toggleMiniSettingLinks}
              miniSettingLinks={miniSettingLinks}
              languages={languages}
              language={language}
                popover={showMobileSidebar ? null : t('video.login_required_modal.button.label')}
              setIsLanguageMenu={setIsLanguageMenu}
              isOpenMenu={isOpenMenu}
                setIsOpenMenu={setIsOpenMenu}
              onClick={() => {
                window.location.href = `${window.location.protocol}//${window.location.host}/login`;
                showMobileSidebar && toggleMobileSidebar();
                return null;
              }}
            />
          )}
          <SidebarChatWithUs mini={!showMobileSidebar} link="#" />
        </>
      }
    />
  );
}

export default ProtectedPageSidebar;
